import { FC, MouseEvent, Ref } from 'react';
import { LoadingSpinner } from 'shared/components/LoadingSpinner/LoadingSpinner';
import { IconType } from 'shared/icons/SvgIcon';
import { mergeClasses } from 'shared/utils/mergeClasses';

type Variant = 'white' | 'danger' | 'primary' | 'gray' | 'light-gray' | 'black';
type Size = 'sm' | 'md' | 'lg' | 'xl';
type Shape = 'rounded' | 'squared';

const getVariantClassNames = (variant: Variant) => {
  switch (variant) {
    case 'white':
      return 'bg-white/10 hover:enabled:bg-white/15 active:enabled:bg-white/20 text-white [&>.spinner]:!border-r-white [&>.spinner]:!border-t-white';
    case 'danger':
      return 'text-red hover:enabled:text-red-600 hover:enabled:bg-black/5 active:enabled:text-red-700 active:enabled:bg-black/10 [&>.spinner]:!border-r-red [&>.spinner]:!border-t-red';
    case 'primary':
      return 'text-primary hover:enabled:text-primary-600 hover:enabled:bg-black/5 active:enabled:text-primary-700 active:enabled:bg-black/10 [&>.spinner]:!border-r-primary [&>.spinner]:!border-t-primary';
    case 'gray':
      return 'text-gray hover:enabled:text-gray-600 hover:enabled:bg-black/5 active:enabled:text-gray-700 active:enabled:bg-black/10 [&>.spinner]:!border-r-gray [&>.spinner]:!border-t-gray';
    case 'light-gray':
      return 'text-gray-300 hover:enabled:text-gray-400 hover:enabled:bg-black/5 active:enabled:text-gray-500 active:enabled:bg-black/10 [&>.spinner]:!border-r-gray-300 [&>.spinner]:!border-t-gray-300';
    case 'black':
      return 'text-black hover:enabled:text-black/70 active:enabled:text-black/50 [&_.spinner]:!border-r-black [&_.spinner]:!border-t-black disabled:text-gray';
    default:
      return '';
  }
};

const getSizeClassNames = (size: Size) => {
  switch (size) {
    case 'sm':
      return 'h-5 w-5 min-w-5 [&>svg]:w-4 [&>svg]:h-4';
    case 'md':
      return 'h-8 w-8 md:h-10 md:w-10 [&>svg]:w-5 [&>svg]:h-5';
    case 'lg':
      return 'h-8 w-8 md:h-10 md:w-10 [&>svg]:w-6 [&>svg]:h-6';
    case 'xl':
      return 'h-10 w-10 md:h-12 md:w-12 [&>svg]:w-8 [&>svg]:h-8';
    default:
      return '';
  }
};

const getShapeClassNames = (shape: Shape) => {
  switch (shape) {
    case 'rounded':
      return 'rounded-full';
    case 'squared':
      return 'rounded-md';
    default:
      return '';
  }
};
interface IconButtonProps {
  name: string;
  icon: IconType;
  ref?: Ref<HTMLButtonElement | null>;
  variant?: Variant;
  size?: Size;
  shape?: Shape;
  type?: HTMLButtonElement['type'];
  ariaLabel?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const IconButton: FC<IconButtonProps> = ({
  name,
  icon: Icon,
  ref,
  variant = 'white',
  size = 'md',
  shape = 'rounded',
  type = 'button',
  ariaLabel,
  onClick,
  loading,
  disabled,
  className = '',
}) => {
  const variantClassNames = getVariantClassNames(variant);
  const sizeClassNames = getSizeClassNames(size);
  const shapeClassName = getShapeClassNames(shape);

  return (
    <button
      ref={ref}
      disabled={disabled}
      className={mergeClasses(
        'flex-center transition-all disabled:cursor-not-allowed disabled:opacity-50',
        variantClassNames,
        sizeClassNames,
        shapeClassName,
        className,
      )}
      aria-label={ariaLabel}
      type={type}
      name={name}
      onClick={onClick}
    >
      {loading ? <LoadingSpinner size={18} /> : <Icon />}
    </button>
  );
};
